import { useCallback, useMemo } from "react";

import { AppMenu, AppMenuSection } from "design-system/AppShell";

import { usePage } from "../../../contexts/PageContext";
import { useFilteredNavTree } from "../useNavItems";

/**
 * Side menu — composes the design-system `<AppMenu>` with the
 * project's permission-filtered navigation tree (shared with the
 * navbar page search via `useFilteredNavTree`).
 *
 * Provenance — `<AppMenu>` is skill-derived; the wiring + section
 * tree are project-specific.
 */
function Sidebar() {
  const { navigate, page } = usePage();
  const tree = useFilteredNavTree();

  const handleLinkClick = useCallback(
    (action: string, url: string = ""): void => {
      if (url !== "") {
        window.open(url, "_blank")?.focus();
        return;
      }
      navigate(action);
    },
    [navigate]
  );

  const sections = useMemo<AppMenuSection[]>(
    () =>
      tree.map((item) => ({
        header: item.header,
        icon: item.icon,
        links: item.links.map((link) => ({
          id: `${item.header}::${link.name}`,
          label: link.name.replace(/^-/, ""),
          active:
            !!link.action &&
            !link.url &&
            link.action === page,
          onClick: link.url
            ? () => handleLinkClick(link.action, link.url)
            : () => handleLinkClick(link.action),
        })),
      })),
    [tree, page, handleLinkClick]
  );

  return <AppMenu sections={sections} />;
}

export default Sidebar;
